import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';

const TIER_COLORS = {
  0: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700', accent: 'bg-gray-500', gradient: 'from-gray-400 to-gray-600' },
  1: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', accent: 'bg-blue-500', gradient: 'from-blue-400 to-blue-600' },
  2: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', accent: 'bg-amber-500', gradient: 'from-amber-400 to-orange-500' },
  3: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', accent: 'bg-purple-500', gradient: 'from-purple-500 to-pink-500' },
};

const TIER_ICONS = {
  0: <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582" /></svg>,
  1: <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>,
  2: <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.841m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" /></svg>,
  3: <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>,
};

const TierDashboardComponent = () => {
  const [tierData, setTierData] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    fetchTierData();
  }, []);

  const fetchTierData = async () => {
    try {
      setLoading(true);
      const [tierRes, refRes] = await Promise.all([
        axios.get(`${API}/tiers/my-tier`),
        axios.get(`${API}/tiers/my-referrals`)
      ]);
      setTierData(tierRes.data);
      setReferralData(refRes.data);
    } catch (err) {
      console.error('Error fetching tier data:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!tierData) return null;

  const tier = tierData.tier;
  const colors = TIER_COLORS[tier] || TIER_COLORS[0];

  return (
    <div className="space-y-6" data-testid="tier-dashboard">
      {/* Header con tier actual */}
      <div className={`rounded-2xl border-2 ${colors.border} ${colors.bg} p-6`} data-testid="tier-card">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white shadow-lg`}>
            {TIER_ICONS[tier]}
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Tu Nivel</p>
            <h2 className={`text-2xl font-bold ${colors.text}`} data-testid="tier-name">{tierData.tier_name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Tier {tier}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Comision</p>
            <p className={`text-2xl font-bold ${colors.text}`} data-testid="tier-commission">{tierData.commission_pct}%</p>
          </div>
        </div>

        {/* Beneficios del tier actual */}
        <div className="mt-4 flex flex-wrap gap-2">
          {tierData.features?.map((f, i) => (
            <span key={i} className={`text-xs px-3 py-1 rounded-full ${colors.accent} text-white font-medium`}>
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Codigo de referido - visible para TODOS los tiers */}
      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="referral-section">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Tu Codigo de Referido</h3>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-gray-50 rounded-lg px-4 py-3 font-mono text-lg font-bold text-center tracking-widest text-gray-800 border border-dashed border-gray-300" data-testid="referral-code">
            {referralData?.referral_code || '---'}
          </div>
          <button
            onClick={copyCode}
            data-testid="copy-referral-btn"
            className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
              codeCopied
                ? 'bg-green-500 text-white'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            {codeCopied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Comparte tu codigo. Cuando alguien lo use y compre WEN, recibes Cc.
          Tu % actual: <span className="font-semibold">{referralData?.current_pct}%</span>
        </p>
        {referralData && referralData.total_referrals > 0 && (
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="text-gray-500">Referidos: <span className="font-semibold text-gray-800">{referralData.total_referrals}</span></span>
            <span className="text-gray-500">Ganado: <span className="font-semibold text-green-600">{referralData.total_earned_cc} Cc</span></span>
          </div>
        )}
      </div>

      {/* Tier 1+: Badge + Progreso al siguiente tier */}
      {tier >= 1 && tierData.progress && Object.keys(tierData.progress).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="tier-progress-section">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Progreso hacia <span className={colors.text}>{tierData.progress.next_tier}</span>
          </h3>
          <div className="space-y-4">
            {tierData.progress.transactions && tierData.progress.transactions.needed > 0 && (
              <ProgressBar
                label="Transacciones (30d)"
                current={tierData.progress.transactions.current}
                needed={tierData.progress.transactions.needed}
                color="blue"
              />
            )}
            {tierData.progress.modules && tierData.progress.modules.needed > 0 && (
              <ProgressBar
                label="Modulos activos"
                current={tierData.progress.modules.current}
                needed={tierData.progress.modules.needed}
                color="amber"
              />
            )}
            {tierData.progress.referrals && tierData.progress.referrals.needed > 0 && (
              <ProgressBar
                label="Referidos activos"
                current={tierData.progress.referrals.current}
                needed={tierData.progress.referrals.needed}
                color="purple"
              />
            )}
          </div>
        </div>
      )}

      {/* Tier 2+: Metricas de actividad */}
      {tier >= 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="tier-metrics-section">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Metricas de Actividad</h3>
          <div className="grid grid-cols-3 gap-4">
            <MetricCard label="TX 30 dias" value={tierData.metrics?.transactions_30d || 0} icon="tx" />
            <MetricCard label="Modulos" value={tierData.metrics?.modules_active || 0} icon="mod" />
            <MetricCard label="Referidos" value={tierData.metrics?.referrals || 0} icon="ref" />
          </div>
          {tierData.monthly_bonus_cc > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center gap-3">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-green-800">Bonus mensual: {tierData.monthly_bonus_cc} Cc</p>
                <p className="text-xs text-green-600">Del pool de comisiones recicladas</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tier 3: Historial de recompensas */}
      {tier >= 3 && referralData?.recent_referrals?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="tier-rewards-section">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Historial de Referidos</h3>
          <div className="space-y-2">
            {referralData.recent_referrals.map((ref, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-sm font-bold">
                    {ref.full_name?.[0] || '?'}
                  </div>
                  <span className="text-sm text-gray-700">{ref.full_name}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {ref.created_at ? new Date(ref.created_at).toLocaleDateString('es-VE', { day: '2-digit', month: 'short' }) : ''}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
            <span className="text-sm text-gray-500">Total ganado</span>
            <span className="text-lg font-bold text-green-600" data-testid="total-earned">{referralData.total_earned_cc} Cc</span>
          </div>
        </div>
      )}

      {/* Escala de referidos - visible para TODOS */}
      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="referral-scale-section">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Escala de Referidos</h3>
        <div className="space-y-2">
          {referralData?.scale?.map((s, i) => {
            const isActive = referralData.total_referrals >= s.min && referralData.total_referrals <= s.max;
            return (
              <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${isActive ? 'bg-purple-50 border border-purple-200 font-semibold' : 'text-gray-500'}`}>
                <span>{s.min}-{s.max > 9999 ? '+' : s.max} referidos</span>
                <span className={isActive ? 'text-purple-700' : ''}>{s.pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({ label, current, needed, color }) => {
  const pct = Math.min(100, (current / needed) * 100);
  const colorMap = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    purple: 'bg-purple-500'
  };
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold text-gray-800">{current}/{needed}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${colorMap[color] || 'bg-blue-500'} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, icon }) => {
  const icons = {
    tx: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" /></svg>,
    mod: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zm0 9.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6z" /></svg>,
    ref: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>,
  };
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <div className="flex justify-center text-gray-400 mb-1">{icons[icon]}</div>
      <p className="text-xl font-bold text-gray-800">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

export default TierDashboardComponent;
